import MenuIcon from '@mui/icons-material/Menu';
import MenuOpenIcon from '@mui/icons-material/MenuOpen';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import AppBar from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import Hidden from '@mui/material/Hidden';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Toolbar from '@mui/material/Toolbar';
import { alpha } from '@mui/material/styles';
import React from 'react';
import { useSelector } from 'react-redux';

import AppLngSwitcher from '../../../AppLngSwitcher';
import AppMessages from '../../../AppMessages';
import AppNotifications from '../../../AppNotifications';
import AppTooltip from '../../../AppTooltip';
import { Button, Grid, Link as MuiLink } from '@mui/material';
import AppLogo from '@crema/components/AppLogo';
import CurrencyExchange from '@mui/icons-material/CurrencyExchange';
import PodAppCurrency from '../../../PodAppCurrency';
import { useNavigate } from 'react-router-dom';
import ImportContactsOutlinedIcon from '@mui/icons-material/ImportContactsOutlined';
import StorefrontOutlinedIcon from '@mui/icons-material/StorefrontOutlined';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import { useIntl } from 'react-intl';
import { useAuthUserQuery } from '@libs/services/auth/src/react-query/useAuthUserQuery';

type Props = {
  toggleNavCollapsed: () => void;
  showDesktopToggle?: boolean;
  isNavExpanded?: boolean;
};
const AppHeader = ({ toggleNavCollapsed, showDesktopToggle = false, isNavExpanded = false }: Props) => {
  const title = useSelector((state: any) => state.app.title);
  const subTitle = useSelector((state: any) => state.app.subTitle);
  const { messages } = useIntl();
  const navigate = useNavigate();

  const isChannelAdmin = process.env.NX_APPLICATION === 'ac';

  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [storeMenuAnchorEl, setStoreMenuAnchorEl] = React.useState<null | HTMLElement>(null);

  const handleClick = (event: any) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const { data: userAuthData } = useAuthUserQuery();

  const handleClickStore = () => {
    if (!userAuthData) return;

    const storeAdminPath: Record<'CAFE24' | 'SHOPBY' | 'NAVER_STORE', string> = {
      CAFE24: 'https://eclogin.cafe24.com/Shop/',
      SHOPBY: 'https://admin.shopby.co.kr/pro/',
      NAVER_STORE: 'https://sell.smartstore.naver.com/',
    };

    const inflowPath = (userAuthData?.channel?.inflowPath || 'CAFE24') as keyof typeof storeAdminPath;
    window.open(storeAdminPath[inflowPath], '_blank');
  };

  const handleOpenStoreMenu = (event: React.MouseEvent<HTMLElement>) => {
    setStoreMenuAnchorEl(event.currentTarget);
  };

  const handleCloseStoreMenu = () => {
    setStoreMenuAnchorEl(null);
  };

  const handleStoreMenuClick = (storeType: 'CAFE24' | 'SHOPBY' | 'NAVER_STORE') => {
    const storeAdminPath: Record<'CAFE24' | 'SHOPBY' | 'NAVER_STORE', string> = {
      CAFE24: 'https://eclogin.cafe24.com/Shop/',
      SHOPBY: 'https://admin.shopby.co.kr/pro/',
      NAVER_STORE: 'https://sell.smartstore.naver.com/',
    };

    window.open(storeAdminPath[storeType], '_blank');
    handleCloseStoreMenu();
  };

  return (
    <AppBar
      position="relative"
      color="inherit"
      sx={{
        boxShadow: 'none',
        borderBottom: (theme) => `solid 1px ${theme.palette.divider}`,
        backgroundColor: 'background.paper',
        width: {
          xs: '100%',
        },
      }}
      className="app-bar"
    >
      <Toolbar
        sx={{
          boxSizing: 'border-box',
          minHeight: { xs: 56, sm: 70 },
          paddingLeft: { xs: 5 },
          paddingRight: { xs: 5, md: 7.5, xl: 12.5 },
        }}
      >
        <Hidden lgUp={!showDesktopToggle}>
          <IconButton
            sx={{
              color: 'text.secondary',
            }}
            edge="start"
            className="menu-btn"
            color="inherit"
            aria-label={showDesktopToggle ? (isNavExpanded ? '메뉴 접기' : '메뉴 펼치기') : 'open drawer'}
            aria-expanded={isNavExpanded}
            title={showDesktopToggle ? (isNavExpanded ? '메뉴 접기' : '메뉴 펼치기') : undefined}
            onClick={toggleNavCollapsed}
            size="large"
          >
            {showDesktopToggle && isNavExpanded ? (
              <MenuOpenIcon sx={{ width: 28, height: 28 }} />
            ) : (
              <MenuIcon sx={{ width: showDesktopToggle ? 28 : 35, height: showDesktopToggle ? 28 : 35 }} />
            )}
          </IconButton>
        </Hidden>
        <Box
          sx={{
            // '& .logo-text': {
            //   display: { xs: 'none', sm: 'block' },
            // },
            width: '100%',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <AppLogo onClick={() => navigate('/main')} />
          {isChannelAdmin && (
            <Box
              sx={{
                display: 'flex',
                gap: 3.5,
                alignItems: 'center',
              }}
            >
              <MuiLink href={'https://podshop.oopy.io'} target="_blank" rel="noopener noreferrer">
                <Button variant="outlined" startIcon={<ImportContactsOutlinedIcon />}>
                  {`${messages['common.guide']}`}
                </Button>
              </MuiLink>
              {/*자사몰이 아닐때만 노출*/}
              {userAuthData &&
                (userAuthData.isCafe24AuthencationUser ||
                  userAuthData.isShopByAuthencationUser ||
                  userAuthData.isNaverSmartStoreAuthencationUser) && (
                  <>
                    <Button
                      variant="outlined"
                      startIcon={<StorefrontOutlinedIcon />}
                      endIcon={<ArrowDropDownIcon />}
                      onClick={handleOpenStoreMenu}
                      aria-controls={storeMenuAnchorEl ? 'store-menu' : undefined}
                      aria-haspopup="true"
                      aria-expanded={storeMenuAnchorEl ? 'true' : undefined}
                    >
                      {`${messages['coomon.store.management']}`}
                    </Button>
                    <Menu
                      id="store-menu"
                      anchorEl={storeMenuAnchorEl}
                      open={Boolean(storeMenuAnchorEl)}
                      onClose={handleCloseStoreMenu}
                      MenuListProps={{
                        'aria-labelledby': 'store-button',
                      }}
                      slotProps={{
                        paper: {
                          sx: {
                            minWidth: storeMenuAnchorEl?.offsetWidth || 'auto',
                          },
                        },
                      }}
                    >
                      {userAuthData.isCafe24AuthencationUser && (
                        <MenuItem onClick={() => handleStoreMenuClick('CAFE24')}>Cafe24 스토어 바로가기</MenuItem>
                      )}
                      {userAuthData.isShopByAuthencationUser && (
                        <MenuItem onClick={() => handleStoreMenuClick('SHOPBY')}>Shopby 스토어 바로가기</MenuItem>
                      )}
                      {userAuthData.isNaverSmartStoreAuthencationUser && (
                        <MenuItem onClick={() => handleStoreMenuClick('NAVER_STORE')}>스마트스토어 바로가기</MenuItem>
                      )}
                    </Menu>
                  </>
                )}
            </Box>
          )}
        </Box>

        {/* <Box
          sx={{
            flexGrow: 1,
          }}
        /> */}
        {/* <Box sx={{ ml: 4 }}>
          <AppLngSwitcher iconOnly={true} tooltipPosition="bottom" />
        </Box> */}

        {/* <Box sx={{ ml: 4 }}>
          <Hidden smDown>
            <Box
              sx={{
                position: 'relative',
                display: 'flex',
                alignItems: 'center',
                marginLeft: -2,
                marginRight: -2,
              }}
            >
              <Box
                sx={{
                  px: 1.85,
                }}
              >
                <AppNotifications />
              </Box>
              <Box
                sx={{
                  px: 1.85,
                }}
              >
                <PodAppCurrency iconOnly={true} tooltipPosition="bottom"/>
              </Box>
            </Box>
          </Hidden>
          <Hidden smUp>
            <Box
              sx={{
                position: 'relative',
                display: 'flex',
                alignItems: 'center',
                marginLeft: -2,
                marginRight: -2,
              }}
            >
              <Box
                sx={{
                  px: 1.85,
                }}
              >
                <AppTooltip title="More">
                  <IconButton
                    sx={{
                      borderRadius: '50%',
                      width: 40,
                      height: 40,
                      color: (theme) => theme.palette.text.secondary,
                      backgroundColor: (theme) =>
                        theme.palette.background.default,
                      border: 1,
                      borderColor: 'transparent',
                      '&:hover, &:focus': {
                        color: (theme) => theme.palette.text.primary,
                        backgroundColor: (theme) =>
                          alpha(theme.palette.background.default, 0.9),
                        borderColor: (theme) =>
                          alpha(theme.palette.text.secondary, 0.25),
                      },
                    }}
                    onClick={handleClick}
                    size="large"
                  >
                    <MoreVertIcon />
                  </IconButton>
                </AppTooltip>
              </Box>
            </Box>
          </Hidden>
        </Box> */}
      </Toolbar>
    </AppBar>
  );
};
export default AppHeader;
